import { notificationsActions } from "@/store/notifications/notifications.slice";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { aventureApi } from "./apiService/aventureApi";
import { axiosService } from "./apiService/axiosService";
import { loadingError } from "./error.service";
import { createClientComponent } from "@/app/utils/supabase/client";

/**
 * Helper function to check if user has a valid Supabase session
 * before making notification API calls
 * @returns Promise<string | null> Access token or null if not authenticated
 */
async function getSupabaseToken(): Promise<string | null> {
  try {
    const supabase = createClientComponent();
    const { data } = await supabase.auth.getSession();

    if (!data.session) {
      console.log("[Notifications] No active Supabase session");
      return null;
    }

    return data.session.access_token;
  } catch (error) {
    console.error("[Notifications] Error checking Supabase session:", error);
    return null;
  }
}

/**
 * Helper function to safely get error message
 */
function getErrorMessage(error: unknown): string {
  if (error === null || error === undefined) return "Unknown error";
  if (typeof error === "string") return error;
  if (typeof error === "object") {
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
    return JSON.stringify(error);
  }
  return String(error);
}

/**
 * Create a proper FetchBaseQueryError object for consistency with RTK Query
 */
function createQueryError(message: string): FetchBaseQueryError {
  return {
    status: "CUSTOM_ERROR",
    error: message,
    data: message,
  };
}

const notificationsApi = aventureApi.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query({
      async queryFn(_, { dispatch }) {
        try {
          // Verify Supabase session first
          const token = await getSupabaseToken();
          if (!token) {
            console.warn(
              "[Notifications] Skipping getNotifications - No valid auth session"
            );
            return { data: [] }; // Return empty data instead of error
          }

          console.log(
            "[Notifications] Fetching notifications with valid session"
          );
          const { data } = await axiosService.get("app/user/info-notification");
          dispatch(notificationsActions.setNotifications(data?.data));
          return { data };
        } catch (error) {
          console.error("[Notifications] Error fetching notifications:", error);
          dispatch(loadingError(error));
          return { error: createQueryError(getErrorMessage(error)) };
        }
      },
      providesTags: ["notifications"],
    }),

    getUnreadNotifications: builder.query({
      async queryFn(_, { dispatch }) {
        try {
          // Verify Supabase session first
          const token = await getSupabaseToken();
          if (!token) {
            console.warn(
              "[Notifications] Skipping getUnreadNotifications - No valid auth session"
            );
            return { data: [] }; // Return empty data instead of error
          }

          console.log(
            "[Notifications] Fetching unread notifications with valid session"
          );
          const { data } = await axiosService.get(
            "app/user/unread-info-notification"
          );
          dispatch(notificationsActions.setUnreadNotifications(data?.data));
          return { data };
        } catch (error) {
          console.error(
            "[Notifications] Error fetching unread notifications:",
            error
          );
          dispatch(loadingError(error));
          return { error: createQueryError(getErrorMessage(error)) };
        }
      },
      providesTags: ["notifications"],
    }),

    markReadNotification: builder.mutation({
      async queryFn(noti_id, { dispatch }) {
        try {
          // Verify Supabase session first
          const token = await getSupabaseToken();
          if (!token) {
            return { error: createQueryError("No valid auth session") };
          }

          const { data } = await axiosService.patch(
            `app/user/info-notification/${noti_id}`
          );
          return { data };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: createQueryError(getErrorMessage(error)) };
        }
      },
      invalidatesTags: ["notifications"],
    }),

    markReadAllNotification: builder.mutation({
      async queryFn(_, { dispatch }) {
        try {
          // Verify Supabase session first
          const token = await getSupabaseToken();
          if (!token) {
            return { error: createQueryError("No valid auth session") };
          }

          const { data } = await axiosService.post(
            `app/user/info-notification/mark-all-read`
          );
          return { data };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: createQueryError(getErrorMessage(error)) };
        }
      },
      invalidatesTags: ["notifications"],
    }),

    deleteNotification: builder.mutation({
      async queryFn(noti_id, { dispatch }) {
        try {
          // Verify Supabase session first
          const token = await getSupabaseToken();
          if (!token) {
            return { error: createQueryError("No valid auth session") };
          }

          const { data } = await axiosService.delete(
            `app/user/info-notification/${noti_id}`
          );
          return { data };
        } catch (error) {
          dispatch(loadingError(error));
          return { error: createQueryError(getErrorMessage(error)) };
        }
      },
      invalidatesTags: ["notifications"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useLazyGetNotificationsQuery,
  useLazyGetUnreadNotificationsQuery,
  useMarkReadNotificationMutation,
  useDeleteNotificationMutation,
  useMarkReadAllNotificationMutation,
} = notificationsApi;
export default notificationsApi;
